import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ILocale, validationMessages } from "infrastructure/localization";
import { INotification, notification } from "infrastructure/notification";
import { actionProcessing } from "infrastructure/store";
import { getScreenExtraReducers } from "ui/app";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";
import { IIndustry } from "ui/shared/models/common";

import { createTextPreview } from "../shared/helpers/TextPreview.helpers";
import { IChangeToken } from "../shared/types/Token.types";

import {
  IPlexTranslationMessagesManagerState,
  IPlexTranslationMessagesManagerFilter,
  getInitialPlexTranslationMessagesManagerState,
  IPlexTranslationMessagesManagerFilterData,
  IPlexTranslationMessageRow,
  IChangeText,
  IPlexTranslationMessageModifyData,
  IAccount
} from "./PlexTranslationMessages.state";

const initialState = getInitialPlexTranslationMessagesManagerState();

export const plexTranslationMessagesManager = createSlice({
  name: "plexTranslationMessagesManager",
  initialState: initialState,
  reducers: {
    loadingFailed: (state, action: PayloadAction<INotification>) => {
      state.isLoading = false;
      state.notifications.push(action.payload);
      return state;
    },

    initialize: state => {
      return { ...state, isLoading: true };
    },

    initializeCompleted: (state, action: PayloadAction<IPlexTranslationMessagesManagerFilter>) => {
      return { ...state, isLoading: false, filter: action.payload };
    },

    search: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    searchCompleted: (state, action: PayloadAction<IPlexTranslationMessageRow[]>) => {
      return {
        ...state,
        isLoading: false,
        grid: { gridData: action.payload, selected: [], isSearched: true },
        isExportDisabled: action.payload.length === 0
      };
    },

    loadIndustries: (state, _: PayloadAction<IPickerSearchCriterion<IIndustry>>): IPlexTranslationMessagesManagerState => {
      return { ...state, isLoading: true, filter: { ...state.filter } };
    },

    loadIndustriesCompleted: (state, action: PayloadAction<IIndustry[]>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, industries: action.payload } };
    },

    loadLocales: (state, _: PayloadAction<IPickerSearchCriterion<ILocale>>): IPlexTranslationMessagesManagerState => {
      return { ...state, isLoading: true, filter: { ...state.filter } };
    },

    loadLocalesCompleted: (state, action: PayloadAction<ILocale[]>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, locales: action.payload } };
    },

    loadSupportedLocales: state => {
      return {
        ...state,
        filter: { ...state.filter, supportedLocales: undefined }
      };
    },

    loadSupportedLocalesCompleted: (state, action: PayloadAction<ILocale[]>) => {
      return { ...state, filter: { ...state.filter, supportedLocales: action.payload } };
    },

    loadSupportedLocalesFailed: (state, action: PayloadAction<INotification>) => {
      return { ...state, filter: { ...state.filter, supportedLocales: [] }, notifications: [...state.notifications, action.payload] };
    },

    loadAssignedToAccounts: (state: IPlexTranslationMessagesManagerState): IPlexTranslationMessagesManagerState => {
      return { ...state, isLoading: true, filter: { ...state.filter } };
    },

    loadAssignedToAccountsCompleted: (state: IPlexTranslationMessagesManagerState, action: PayloadAction<IAccount[]>): IPlexTranslationMessagesManagerState => {
      return { ...state, isLoading: false, filter: { ...state.filter, assignedToAccounts: action.payload } };
    },

    loadAssignedToAccountsFailed: (state: IPlexTranslationMessagesManagerState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, assignedToAccounts: [] }, notifications: [...state.notifications, action.payload] };
    },

    loadPickerAccounts: (state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true, actionBar: { ...state.actionBar } };
    },

    loadPickerAccountsCompleted: (state: IPlexTranslationMessagesManagerState, action: PayloadAction<IAccount[]>) => {
      return { ...state, isLoading: false, actionBar: { ...state.actionBar, accountPickerData: action.payload, notifications: [] } };
    },

    loadPickerAccountsFailed: (state: IPlexTranslationMessagesManagerState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, actionBar: { accountPickerData: [], notifications: [action.payload] } };
    },

    setValidationBanner: (state: IPlexTranslationMessagesManagerState) => {
      return { ...state, actionBar: { ...state.actionBar, notifications: [notification.warning().useMessageId(validationMessages.error("summary"))] } };
    },

    clearNotificationsActionBar: (state: IPlexTranslationMessagesManagerState) => {
      return { ...state, actionBar: { ...state.actionBar, notifications: [] } };
    },

    selectAccount: (state: IPlexTranslationMessagesManagerState, action: PayloadAction<IAccount | undefined>) => {
      return {
        ...state,
        actionBar: { ...state.actionBar, accountSelected: action.payload }
      };
    },

    selectPlexTranslations: (state: IPlexTranslationMessagesManagerState, action: PayloadAction<IPlexTranslationMessageRow[]>) => {
      return {
        ...state,
        grid: { ...state.grid, selected: action.payload }
      };
    },

    changePlexTranslation: (state, _action: PayloadAction<IChangeText>) => {
      return state;
    },

    changeTokenValue: (state, _action: PayloadAction<IChangeToken>) => {
      return {
        ...state
      };
    },

    changeCommentText: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        actionBar: {
          ...state.actionBar,
          review: {
            comment: action.payload
          }
        }
      };
    },

    applyChangeTokenValue: (state, { payload }: PayloadAction<IChangeToken>) => {
      const gridData = [...state.grid.gridData];
      const row = gridData[payload.rowIndex];
      const tokens = [...row.data.tokens];
      const token = row.data.tokens[payload.tokenIndex];

      tokens[payload.tokenIndex] = {
        index: token.index,
        name: token.name,
        description: token.description,
        type: token.type,
        value: payload.value,
        displayValue: payload.displayValue
      };

      const translatedText = row.data.translatedText ? row.data.translatedText : "";
      const textPreview = createTextPreview(translatedText, tokens, row.data.locale);

      gridData[payload.rowIndex] = {
        ...row,
        data: {
          ...row.data,
          tokens: tokens,
          textPreview: textPreview
        }
      };

      const selected: IPlexTranslationMessageRow[] = state.grid.selected.length ? gridData.filter(d => state.grid.selected.find(s => s.index === d.index)) : [];

      return { ...state, grid: { ...state.grid, gridData: gridData, selected: [...selected] } };
    },

    applyPlexTranslationChange: (state, { payload }: PayloadAction<IPlexTranslationMessageModifyData>) => {
      const gridData = [...state.grid.gridData];
      const row = gridData[payload.index];

      const translatedText = payload.translatedText ? payload.translatedText : "";
      const textPreview = createTextPreview(translatedText, row.data.tokens, row.data.locale);

      gridData[payload.index] = {
        ...row,
        originalData: payload.originalData,
        isTranslationChanged: payload.isTranslationChanged,
        validationIssues: payload.validationIssues,
        data: {
          ...row.data,
          translatedText: payload.translatedText,
          messageId: payload.messageId,
          textPreview: textPreview
        }
      };

      const selected: IPlexTranslationMessageRow[] = state.grid.selected.length ? gridData.filter(d => state.grid.selected.find(s => s.index === d.index)) : [];

      return { ...state, grid: { ...state.grid, gridData: gridData, selected: [...selected] } };
    },

    applyPlexTranslationsChanges: (state, { payload }: PayloadAction<IPlexTranslationMessageModifyData[]>) => {
      const gridData = [...state.grid.gridData];

      payload.forEach(data => {
        const row = gridData[data.index];

        gridData[data.index] = {
          ...row,
          originalData: data.originalData,
          isTranslationChanged: data.isTranslationChanged,
          validationIssues: data.validationIssues,
          data: {
            ...row.data,
            translatedText: data.translatedText,
            messageId: data.messageId
          }
        };
      });

      const selected: IPlexTranslationMessageRow[] = state.grid.selected.length ? gridData.filter(d => state.grid.selected.find(s => s.index === d.index)) : [];

      return { ...state, grid: { ...state.grid, gridData: gridData, selected: [...selected] } };
    },

    applyPlexTranslationChangesWarning: (state, action: PayloadAction<INotification | undefined>) => {
      if (action.payload) {
        return { ...state, notifications: [action.payload] };
      } else {
        return { ...state, notifications: [] };
      }
    },

    savePlexTranslations: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    savePlexTranslationsCompleted: (state, { payload }: PayloadAction<{ message: INotification; isLoading?: boolean }>) => {
      if (payload.isLoading !== undefined) {
        state.isLoading = payload.isLoading;
      }
      state.notifications.push(payload.message);
      return state;
    },

    applyFilter: (state: IPlexTranslationMessagesManagerState, action: PayloadAction<IPlexTranslationMessagesManagerFilterData>) => {
      return {
        ...state,
        isLoading: false,
        filter: { ...state.filter, lastData: action.payload }
      };
    },

    exportPlexTranslationsToXml: (state: IPlexTranslationMessagesManagerState) => {
      return state;
    },

    approvePlexTranslations: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    approvePlexTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload], grid: { ...state.grid, selected: [] } };
    },

    requestPlexTranslations: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    requestPlexTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload], grid: { ...state.grid, selected: [] } };
    },

    reassignPlexTranslations: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    reassignPlexTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload], grid: { ...state.grid, selected: [] } };
    },

    reviewPlexTranslations: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    reviewPlexTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return {
        ...state,
        notifications: [action.payload],
        grid: { ...state.grid, selected: [] },
        actionBar: { ...state.actionBar, review: { comment: undefined } }
      };
    },

    proposePlexTranslations: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    proposePlexTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload], grid: { ...state.grid, selected: [] } };
    },

    rejectPlexTranslations: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    rejectPlexTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload], grid: { ...state.grid, selected: [] } };
    },

    obsoletePlexTranslations: actionProcessing.createWithClearNotification((state: IPlexTranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    obsoletePlexTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload], grid: { ...state.grid, selected: [] } };
    },

    downloadPlexTranslationUploadTemplate: (state: IPlexTranslationMessagesManagerState) => {
      return {
        ...state
      };
    },

    onPlexTranslationUploadCompleted: (state, action: PayloadAction<INotification>) => {
      return {
        ...state,
        notifications: [action.payload]
      };
    }
  },
  extraReducers: getScreenExtraReducers<IPlexTranslationMessagesManagerState>("notifications", initialState)
});
